import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "@/lib/cookieConsent";

const GA4_ID = process.env.REACT_APP_GA4_ID;
const GTM_ID = process.env.REACT_APP_GTM_ID;

/**
 * Google Analytics 4 + Google Tag Manager loader.
 *
 * Scripts are injected via useEffect (not react-helmet-async) so they are
 * guaranteed to execute even when added AFTER the initial page render, which
 * is exactly what happens right after the user gives cookie consent.
 *
 * Also fires an explicit `page_view` event on every SPA route change so that
 * navigation between React Router routes shows up in GA4 Realtime.
 *
 * Statistics-consent → GA4. Marketing-consent → GTM.
 */
export const Analytics = () => {
  const { consent, hasChosen } = useCookieConsent();
  const location = useLocation();

  const gaEnabled = !!(hasChosen && consent.statistics && GA4_ID);
  const gtmEnabled = !!(hasChosen && consent.marketing && GTM_ID);

  const gaInstalledRef = useRef(false);
  const gtmInstalledRef = useRef(false);

  // Install GA4 exactly once, when statistics consent is active
  useEffect(() => {
    if (!gaEnabled || gaInstalledRef.current) return;
    gaInstalledRef.current = true;

    // 1) initialise dataLayer + gtag stub BEFORE the async loader,
    //    so calls made while gtag.js is downloading are queued correctly.
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", GA4_ID, {
      anonymize_ip: true,
      send_page_view: true,
    });

    // 2) load the gtag.js runtime
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    s.setAttribute("data-fk-ga4", "1");
    document.head.appendChild(s);
  }, [gaEnabled]);

  // Install GTM exactly once, when marketing consent is active
  useEffect(() => {
    if (!gtmEnabled || gtmInstalledRef.current) return;
    gtmInstalledRef.current = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    s.setAttribute("data-fk-gtm", "1");
    document.head.appendChild(s);
  }, [gtmEnabled]);

  // Fire a GA4 page_view on every SPA route change (skip first — config already sent one)
  const firstRouteRef = useRef(true);
  useEffect(() => {
    if (!gaEnabled || typeof window.gtag !== "function") return;
    if (firstRouteRef.current) {
      firstRouteRef.current = false;
      return;
    }
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search, gaEnabled]);

  return null;
};
